import React from 'react';
import './Plans.css';

const INVESTMENT_PLANS = [
  {
    icon: '🌱',
    name: 'Starter Plan',
    tagline: 'Perfect for Beginners',
    minCapital: '₹10,000',
    maxCapital: '₹50,000',
    expectedReturns: '20-30%',
    period: 'per month',
    risk: 'High',
    riskColor: '#10b981',
    markets: ['Large Cap Indian Stocks', 'Blue Chip Cryptos (BTC/ETH)', 'Index Funds'],
    strategy: 'Conservative long-term wealth building with low-risk, high-quality assets.',
    whatsIncluded: ['Weekly Investment Calls', 'SIP Guidance', 'Monthly Review', 'Telegram Access'],
  },
  {
    icon: '📈',
    name: 'Growth Plan',
    tagline: 'Balance Risk & Reward',
    minCapital: '₹50,000',
    maxCapital: '₹2,00,000',
    expectedReturns: '10-20%',
    period: 'per month',
    risk: 'Medium',
    riskColor: '#f0b429',
    markets: ['Mid & Large Cap Stocks', 'F&O Strategies', 'Top 10 Altcoins', 'Swing Trades'],
    strategy: 'Balanced mix of growth stocks and crypto for consistent monthly returns.',
    whatsIncluded: ['Daily Trading Signals', 'F&O Calls', 'Weekly Webinar', 'Priority Support', 'Portfolio Review'],
    popular: true,
  },
  {
    icon: '🚀',
    name: 'Pro Trader Plan',
    tagline: 'Maximum Returns Potential',
    minCapital: '₹2,00,000+',
    maxCapital: 'No Limit',
    expectedReturns: '8-15%',
    period: 'per month',
    risk: 'Low',
    riskColor: '#ef4444',
    markets: ['All Market Segments', 'Futures & Options', 'Crypto Futures', 'IPO & Pre-IPO'],
    strategy: 'Aggressive growth strategy for experienced investors. High risk, high reward.',
    whatsIncluded: ['Premium Signals', '1-on-1 Mentoring', 'Personalized Plan', 'VIP Telegram', 'Direct Phone Support', 'Custom Portfolio'],
  },
];

const HOW_IT_WORKS = [
  { step: '01', title: 'Join & Onboard', desc: 'Contact us on WhatsApp. We understand your goals, capital, and risk appetite.' },
  { step: '02', title: 'Pick Your Plan', desc: 'Choose from Starter, Growth, or Pro Trader based on your capital and goals.' },
  { step: '03', title: 'Get Your Calls', desc: 'Receive precise buy/sell signals with entry, stop loss, and target prices.' },
  { step: '04', title: 'Track & Profit', desc: 'Monitor your portfolio, get weekly reviews, and compound your returns.' },
];

const Plans = () => {
  const WHATSAPP_NUMBER = '919926749937';

  return (
    <div className="plans-page">
      <section className="page-hero section-padding grid-bg">
        <div className="hero-glow-small" />
        <div className="container">
          <div className="tag">Investment Plans</div>
          <h1 className="section-title">Invest Smart,<br /><span className="gold-text">Grow Consistently</span></h1>
          <p className="section-subtitle">
            Structured investment plans designed for every type of investor.
            From beginners to seasoned traders — we have a plan for you.
          </p>
          <div className="disclaimer-banner">
            ⚠️ Disclaimer: Returns are estimated based on historical performance. Markets involve risk. Please invest wisely.
          </div>
        </div>
      </section>

      {/* Investment Plans */}
      <section className="inv-plans-section section-padding">
        <div className="container">
          <div className="inv-plans-grid">
            {INVESTMENT_PLANS.map((plan, i) => (
              <div className={`inv-plan-card card ${plan.popular ? 'featured' : ''}`} key={i}>
                {plan.popular && <div className="plan-featured-label">⭐ Most Popular</div>}
                <div className="inv-plan-header">
                  <span className="inv-plan-icon">{plan.icon}</span>
                  <div>
                    <h3 className="inv-plan-name">{plan.name}</h3>
                    <p className="inv-plan-tagline">{plan.tagline}</p>
                  </div>
                </div>
                <div className="inv-returns">
                  <div className="returns-main">
                    <span className="returns-val">{plan.expectedReturns}</span>
                    <span className="returns-period">{plan.period}</span>
                  </div>
                  <span className="returns-label">Expected Returns</span>
                </div>
                <div className="inv-capital">
                  <div className="capital-item">
                    <span className="cap-label">Min Capital</span>
                    <span className="cap-val">{plan.minCapital}</span>
                  </div>
                  <div className="capital-div" />
                  <div className="capital-item">
                    <span className="cap-label">Max Capital</span>
                    <span className="cap-val">{plan.maxCapital}</span>
                  </div>
                  <div className="capital-div" />
                  <div className="capital-item">
                    <span className="cap-label">Risk Level</span>
                    <span className="cap-val risk-val" style={{ color: plan.riskColor }}>{plan.risk}</span>
                  </div>
                </div>
                <div className="inv-markets">
                  <h4>Markets Covered</h4>
                  {plan.markets.map((m, j) => (
                    <div className="market-item" key={j}>
                      <span style={{ color: plan.riskColor }}>◆</span>
                      <span>{m}</span>
                    </div>
                  ))}
                </div>
                <p className="inv-strategy">{plan.strategy}</p>
                <div className="inv-includes">
                  <h4>What's Included</h4>
                  {plan.whatsIncluded.map((item, j) => (
                    <span className="include-chip" key={j}>{item}</span>
                  ))}
                </div>
                <a
                  href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(`Hi Kalpnic Trading! I'm interested in the ${plan.name} investment plan.`)}`}
                  target="_blank"
                  rel="noreferrer"
                  className={plan.popular ? 'btn-gold plan-btn' : 'btn-outline plan-btn'}
                >
                  Get Started
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="how-it-works section-padding">
        <div className="container">
          <div className="section-header">
            <div className="tag">How It Works</div>
            <h2 className="section-title">Start Your Investment<br /><span className="gold-text">Journey in 4 Steps</span></h2>
          </div>
          <div className="steps-grid">
            {HOW_IT_WORKS.map((step, i) => (
              <div className="step-card" key={i}>
                <div className="step-number">{step.step}</div>
                <h3>{step.title}</h3>
                <p>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Plans;
